<template>
  <div class="bg-white h-auto px-5 rounded-lg">
    <div class="flex flex-col md:flex-row justify-between items-center">
      <StudentPerformanceFilter
        class="self-start"
        :filter-option="isSelectedRoleExist"
        :csv-filter-options="displayedFilterOptions"
        :already-selected-displayed-columns="filtersData?.displayedColumns"
        @apply="applyFilters"
      />
      <CalendarActionsWrapper
        class="self-end"
        :is-future-date-disabled="monthRange"
        :calendar-date="selectedDate"
        filter-view="Month-range"
        custom-class="right-0 before:right-12"
        @selectDate="onDateChange"
      />
    </div>

    <div class="overflow-auto">
      <GraphView :chart-data="graphData" class="min-w-[500px]" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import StudentPerformanceFilter from '@/src/router/views/exam-planner/grades-manager/StudentPerformanceFilter.vue'
import GraphView from '@src/router/views/graph/graphView.vue'
import CalendarActionsWrapper from '@src/components/Calendar/CalendarActionsWrapper.vue'
import { useStore } from 'vuex'
import DATETIME_CONSTANT from '@src/constants/date-time-constants.js'

import {
  formatRangeToFirstAndLastDay,
  getBackDate,
  formatDateFromLocalToUTC,
} from '@src/utils/moment.util.js'
import moment from 'moment'

const selectedCampus = ref('')
const currentCampusScope = computed(() => store.state.layout.currentCampusScope)
const store = useStore()
const monthRange = ref(3)
const dateRange = ref({
  startDate: getBackDate(moment().toDate(), 6, 'days'),
  endDate: moment().toDate(),
})
const selectedDate = ref({
  monthRange: monthRange,
  end: moment().toDate(),
  start: getBackDate(moment().toDate(), monthRange.value - 1, 'months'),
})
const startAndLastDay = computed(() => {
  return formatRangeToFirstAndLastDay(selectedDate.value)
})
const onDateChange = (date) => {
  selectedDate.value = date

  generateExpenseGraphStats()
}

const generateExpenseGraphStats = async () => {
  let payload = {
    start_date: formatDateFromLocalToUTC(selectedDate.value.start, DATETIME_CONSTANT.DATE),
    end_date: formatDateFromLocalToUTC(selectedDate.value.end, DATETIME_CONSTANT.DATE),
  }
  getstudentExamProgress(payload).then((response) => {
    graphData.value.data.datasets = transformResToDatasets(response[0].data)
    ;(graphData.value.options.scales.x.min = startAndLastDay.value.startDate),
      (graphData.value.options.scales.x.max = startAndLastDay.value.endDate)
  })
  if (currentCampusScope.value) selectedCampus.value = currentCampusScope.value.id
}
onMounted(() => {
  generateExpenseGraphStats()
})
const transformResToDatasets = (response) => {
  const subjectColors = {
    'primary-purple': '#7f56d9',
    success: '#039855',
    warning: '#dc6803',
    'secondary-blue-gray': '#3e4784',
    'secondary-blue-light': '#0086c9',
    'secondary-orange': '#ec4a0a',
    'secondary-indigo': '#444ce7',
  }

  return response.stats.map((subject) => {
    const Data = subject.marks
      .map((mark) => ({
        x: mark.Date.split('T')[0],
        y: mark.marks_percentage,
        exam: mark.exam_title,
      }))
      .sort((a, b) => new Date(a.x) - new Date(b.x))

    return {
      label: subject.subject_name,
      data: Data,
      borderColor: subjectColors[subject.subject_color] || 'gray',
      fill: false,
    }
  })
}

const graphData = ref({
  afterTitle: true,
  type: 'line',
  data: {
    labels: [],
    datasets: [],
  },
  options: {
    responsive: true,

    plugins: {
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#111827',
        bodyColor: '#374151',
      },
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          boxWidth: 8,
          boxHeight: 8,
          usePointStyle: true,
          padding: 20,
          font: {
            size: 14,
          },
        },
      },
    },
    hoverRadius: 6,
    interaction: {
      mode: 'nearest',
      intersect: false,
    },
    scales: {
      x: {
        type: 'time',
        min: null,
        max: null,
        time: {
          unit: 'month',
          tooltipFormat: 'DD MMM yyyy',
          displayFormats: {
            month: 'MMM yyyy',
          },
        },
        grid: {
          display: true,
        },
        title: {
          display: true,
          text: 'Date',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
      },
      y: {
        min: 0,
        max: 100,
        ticks: {
          stepSize: 10,
        },
        grid: {},
        title: {
          display: true,
          text: 'Marks',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
      },
    },
  },
})
const getstudentExamProgress = (payload) => store.dispatch('exams/studentExamProgress', payload)
</script>
